import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Logger,
  removeClassesForHidingElements,
  useDetectScroll,
  useLocalize,
  useResizeObserver,
  useResponsiveValueState,
} from '@saviynt/common';
import {
  AlertBanner,
  Badge,
  Box,
  Button,
  ButtonSelect,
  Checkbox,
  FooterActionBar,
  H3,
  Icon,
  InlineMessage,
  InputField,
  Link,
  Loader,
  MenuMulti,
  Typography,
} from '@saviynt/design-system';
import { mfaPrompt } from '@saviynt/nav';
import classnames from 'classnames';

import CheckInModal from '../../components/CheckInModal/CheckInModal';
import Extend from '../../components/Extend/Extend';
import ListRowWrapper from '../../components/LandingPage/ListRowWrapper';
import LandingPageSidePanel from '../../components/LandingPageSidePanel/LandingPageSidePanel';
import { MODAL_PAGE_KINDS } from '../../components/ModalPageForms/constants';
import MoreRecordsMessage from '../../components/MoreRecordsMessage/MoreRecordsMessage';
import SessionLauncher, {
  generateUrl,
} from '../../components/SessionLauncher/SessionLauncher';
import SessionTargetDetailsModalIsland from '../../components/SessionLauncher/SessionTargetDetailsModalIsland/SessionTargetDetailsModalIsland';
import useFetchListRowData from '../../hooks/use-fetchlistrowdata';
import useSessionStatusRefetch from '../../hooks/use-sessionStatusRefetch';
import {
  msgs,
  REQUEST_BASE_URL,
  SCREEN_WIDTH_QUERIES,
} from '../../misc/constants';
import { mockData } from '../../models/MockData/MockData';
import LandingPageFilterService from '../../services/LandingPage/LandingPageFilterService';
import LandingPagePaginationService from '../../services/LandingPage/LandingPagePaginationService';
import {
  clearFileUploadStatus,
  clearRequestSubmittedId,
  listAPIListReceived,
  selectedListAccountRemoteMetadatas,
  selectedListDetails,
  selectedListIsExclusiveAccess,
  selectedListMaxCredlessSessionRequestTime,
} from '../../store/actions';
import {
  clearSelectedRoles,
  updateSelectedRoles,
} from '../../store/actions/bulkRoleSelectAction';
import { updateSearchValue } from '../../store/actions/landingPageFiltersAction';
import {
  hasSelectedRoles,
  isApp,
  isEndpoint,
  isRole,
  isSession,
} from '../../utilities';
import { LISTROW_TYPES } from '../../utilities/constants';
import displayAlertBannersForRequestSubmit from '../../utilities/displayAlertBannersForRequestSubmit';
import hideHostElementsWrapper, {
  removeDynamicBodyClasses,
} from '../../utilities/hideHostElements';

import { renderFilterPills } from './helpers';

import './LandingPage.css';

function LandingPage() {
  const [currentListItem, setCurrentListItem] = useState(null);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [listRowData, setListRowData] = useState({});
  const [isFilterPillsVisible, setIsFilterPillsVisible] = useState(true);
  // Filter / Search
  const savedSearchValue = useSelector(
    (state) => state.landingPageFilters?.searchValue || ''
  );
  const [searchBarValue, setSearchBarValue] = useState(savedSearchValue);
  const [filteredEntities, setFilteredEntities] = useState(null);
  // Check In Modal
  const [isCheckInModalOpen, setIsCheckInModalOpen] = useState(false);
  const [checkInModalKind, setCheckInModalKind] = useState('');
  const [iconClickedForRow, setIconClickedForRow] = useState(null);
  // Hide Session Checkbox
  const [isHideSessionsChecked, setisHideSessionsChecked] = useState(false);
  const [alertBannerData, setAlertBannerData] = useState([]);
  // Extend Modal
  const [isExtendModalOpen, setIsExtendModalOpen] = useState(false);
  const [recordsCount, setRecordsCount] = useState(0);
  const [mediaQueryValue, setMediaQueryValue] = useState(0);
  const [sidePanelWidth, setSidePanelWidth] = useState(0);
  const [endpointOrRoleEntities, setEndpointOrRoleEntities] = useState([]);

  const sidePanelRef = useResizeObserver((rect) => {
    setSidePanelWidth(rect.width);
  });

  const isJitForApps = window.location.pathname === '/ECMv6/request/jitAccess';

  useResponsiveValueState(setMediaQueryValue, SCREEN_WIDTH_QUERIES);

  const MAX_RECORDS = 100;

  // Launch Session Form
  const [sessionToLaunch, setSessionToLaunch] = useState(null);
  // TODO: Needs to be set by some condition and name changed. Used here for DX and ready for implementation.
  const [isSessionTargetModalVisible, setIsSessionTargetModalVisible] =
    useState(false);
  const [sessionDetailsForAd, setSessionDetailsForAd] = useState(null);
  const localize = useLocalize();
  const verify = mfaPrompt.useVerify();

  const { requestId, isRoleRequest } = useSelector(
    (state) => state.requestSubmittedDetails
  );
  const isMenuPinned = useSelector((state) => state.user?.isMenuPinned);
  const isLastRequestFileUploadFailed =
    useSelector((state) => state.fileUploadStatus.uploadFailed) || false;

  const { selectedRoles } = useSelector(
    (state) => state.bulkSelectedRole || { selectedRoles: [] }
  );

  const { selectedFilters } = useSelector(
    (state) => state.landingPageFilters || { selectedFilters: {} }
  );

  const [cookies] = useCookies(['user_name', 'leftNavGen2']);
  const userName = cookies.user_name;
  const { leftNavGen2 } = cookies;

  const handleChange = (event) => {
    setisHideSessionsChecked(event.target.checked);
  };

  // References
  const landingPageContentRef = useRef(null);
  const sessionTargetDetailsModalContentRef = useRef(null);
  const dispatch = useDispatch();
  const history = useHistory();

  const isUsingMockData = false; // TODO: remove mock data when features are complete.

  const verifyMfaAndSetSessionToLaunch = (session) => {
    verify(generateUrl(session).wettyLink, 'POST').then((isVerified) => {
      if (isVerified) {
        setSessionToLaunch(session);
      }
    });
  };

  // Filter Dependencies
  const { platform, asset, role, isFilterChecked, clearAllFilters } =
    LandingPageFilterService(
      setIsSidePanelOpen,
      setCurrentListItem,
      hasSelectedRoles(selectedRoles)
    );

  const { checkedPlatforms, checkedAssets, checkedRoles } = selectedFilters;

  const {
    isListRowApiLoading,
    isListRowApiError,
    listRowApiError,
    isListRowApiSuccess,
  } = useFetchListRowData(
    searchBarValue,
    checkedPlatforms,
    checkedAssets,
    checkedRoles,
    setListRowData,
    isUsingMockData,
    hasSelectedRoles(selectedRoles),
    isJitForApps
  );

  // Pagination Dependencies
  const { pageIndex, paginatedEntities, setPaginatedEntities } =
    LandingPagePaginationService(
      filteredEntities,
      hasSelectedRoles(selectedRoles)
    );

  // Custom hook to refetch the current status at an interval of 5 seconds
  useSessionStatusRefetch({
    isListRowApiSuccess,
    paginatedEntities,
    setPaginatedEntities,
  });

  useEffect(() => {
    setEndpointOrRoleEntities(
      paginatedEntities?.filter((entity) => {
        if (isJitForApps) {
          return (
            !isSession(entity) &&
            (isRole(entity) || (isEndpoint(entity) && isApp(entity)))
          );
        }

        const isWithoutSessionCondition = !isSession(entity);
        const isRoleOrEndpointCondition = isRole(entity) || isEndpoint(entity);

        return isWithoutSessionCondition && isRoleOrEndpointCondition;
      })
    );
  }, [paginatedEntities, selectedRoles, selectedFilters]);

  // Function to update selectedRoles
  const handleCheckboxChange = (selectedRole, isChecked) => {
    dispatch(updateSelectedRoles(selectedRole));

    if (selectedRoles?.length === 1 && !isChecked) {
      role.setSelected([]);
    } else if (!hasSelectedRoles(selectedRoles)) {
      role.setSelected([
        {
          isChecked: true,
          label: 'Emergency Access',
          prefixIcon: {
            isApplicationIcon: false,
            kind: 'Role',
            size: 'medium',
          },
          value: 'EMERGENCYACCESSROLE',
        },
      ]);
    }
  };

  const contentClasses = classnames(
    'LandingPage-content',
    isSidePanelOpen && 'LandingPage-content--withSidePanel'
  );

  const titleContainerClasses = classnames(
    'LandingPage-titleContainer',
    isSidePanelOpen && 'LandingPage-titleContainer--withSidePanel'
  );

  const listRowContainerClasses = classnames(
    'LandingPage-listRows',
    isSidePanelOpen && 'LandingPage-listRows--withSidePanel'
  );

  // PAM Localizations
  const assetsNotLoadedHeader = localize(
    msgs.pam.landingPage.errors.assetsNotLoaded
  );
  const assetsNotLoadedDesc = localize(
    msgs.pam.landingPage.errors.assetsNotLoadedDesc
  );
  const noMatchingSearchRecordMsg = localize(
    msgs.pam.landingPage.noMatchingSearchRecords
  );
  const noAssetsAvailableMsg = localize(msgs.pam.landingPage.noAssetsAvailable);
  const sessionsHeader = localize(msgs.pam.landingPage.sessionsHeader);
  const availableAssetsHeader = localize(
    msgs.pam.landingPage.availableAssetsHeader
  );
  const landingPageTitleDefault = localize({
    id: 'pam.landingPage.title',
    defaultMessage: 'Privileged Access Assets & Sessions',
  });
  const landingPageDescDefault = localize(msgs.pam.landingPage.description);
  const searchInputPlaceholderText = localize(
    msgs.pam.landingPage.userAccessSearchInputPlaceholder
  );
  const hideSessionsLabel = localize(msgs.pam.landingPage.hideSessions);

  // JIT for Apps (JFA) Localizations
  const landingPageTitleJfa = localize({
    id: 'pam.landingPage.titleJfa',
    defaultMessage: 'Just-In-Time Access',
  });
  const landingPageDescJfa = localize(msgs.pam.landingPage.descriptionJfa);
  const searchInputPlaceholderTextJfa = localize(
    msgs.pam.landingPage.userAccessSearchInputPlaceholderJfa
  );

  const checkOutRoles = localize(msgs.pam.landingPage.checkOutRoles);

  const rowIsSelected = (listItem) => {
    if (isSession(listItem) && isSidePanelOpen) {
      return currentListItem?.requestAccessKey === listItem?.requestAccessKey;
    }

    return currentListItem === listItem;
  };

  const listRowOnClick = (row) => {
    setIsSidePanelOpen(true);
    setCurrentListItem(row);
  };

  useEffect(() => {
    // Reset AccountRemoteMetadata and Exclusive Access on mount
    dispatch(selectedListAccountRemoteMetadatas(null, 'reset success'));
    dispatch(selectedListIsExclusiveAccess(false, 'reset success'));
    // TODO: Make sure from AC that this should be 10 hours
    dispatch(
      selectedListMaxCredlessSessionRequestTime('36000', 'reset success')
    );

    removeDynamicBodyClasses();

    if (requestId) {
      displayAlertBannersForRequestSubmit(
        requestId,
        userName,
        isLastRequestFileUploadFailed,
        setAlertBannerData,
        msgs,
        localize,
        isRoleRequest
      );
      dispatch(clearRequestSubmittedId('success'));
      dispatch(clearFileUploadStatus());
    }

    // TODO: remove the next 8 lines when we're only using the new unified nav.
    if (leftNavGen2 === '0') {
      const alertBannersContainer = document.querySelector(
        '.LandingPage-alertBannerContainer'
      );

      alertBannersContainer.style.left = '3rem';
      alertBannersContainer.style.right = '3rem';
    }

    // hide host toast messages, etc when flag is set
    hideHostElementsWrapper();

    return () => {
      removeClassesForHidingElements();
    };
  }, []);

  useEffect(() => {
    // TODO: Ask the team, should we use this for all the state handling for this page now that we have it stored in redux? So we don't have repeated selected listRow states. I need it in Redux for the COC and session headers and account endpointKey.
    dispatch(selectedListDetails(currentListItem, 'success'));
  }, [currentListItem]);

  const sidePanelOnClose = () => {
    setIsSidePanelOpen(false);
    setCurrentListItem(null);
  };

  useEffect(() => {
    // Close the sidepanel when page is changed or search made
    setIsSidePanelOpen(false);
    setCurrentListItem(null);
  }, [pageIndex, searchBarValue]);

  const { isScrolled: sessionTargetDetailsModalSectionRefIsScrolled } =
    useDetectScroll(null, sessionTargetDetailsModalContentRef);

  useEffect(() => {
    if (isUsingMockData) {
      Logger.log('Using mock data on localhost', mockData);
      setListRowData(mockData);
    }

    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  useEffect(() => {
    if (listRowData && Object.keys(listRowData).length !== 0) {
      setFilteredEntities(listRowData);
      setIsSidePanelOpen(false);
    }

    setRecordsCount(listRowData.count);

    // To get data stored in redux store on getting response from API
    if (listRowData && Object.keys(listRowData).length > 0) {
      dispatch(listAPIListReceived(listRowData, 'success'));
    }
  }, [listRowData]);

  useEffect(() => {
    const sidePanel = sidePanelRef.current;

    if (sidePanel) {
      landingPageContentRef.current.style.width = `calc(100% - ${sidePanelWidth}px + 2px)`;
    } else {
      landingPageContentRef.current.style.width = '';
    }
  }, [sidePanelWidth, isSidePanelOpen]);

  useEffect(() => {
    if (isListRowApiError) {
      setAlertBannerData((prevState) => [
        ...prevState,
        {
          colorTheme: 'Critical',
          title: assetsNotLoadedHeader,
          description: assetsNotLoadedDesc,
          shouldAutoDismiss: false,
        },
      ]);
    }
  }, [isListRowApiError, listRowApiError]);

  const renderListOrNoData = () => {
    if (!paginatedEntities) {
      return null;
    }

    if (isListRowApiLoading) {
      return (
        <div className='LandingPage-loaderContainer'>
          <Loader kind='loop' format='main' color='brand' size='large' />
        </div>
      );
    }

    if (paginatedEntities.length === 0) {
      return (
        <InlineMessage className='LandingPage-noMatchingSearchRecordsWrapper'>
          <Box className='LandingPage-noMatchingSearchRecords'>
            <Icon kind='AlertCriticalOutline' />
            <Typography
              kind='h3'
              className='LandingPage-noMatchingSearchRecords-title'>
              {noMatchingSearchRecordMsg}
            </Typography>
          </Box>
        </InlineMessage>
      );
    }

    // Filter and segregate paginated Entities/Assets
    const sessionEntities = paginatedEntities.filter((entity) => {
      if (isJitForApps) {
        if (isSession(entity)) {
          return isRole(entity) || isApp(entity);
        }
      }

      return isSession(entity);
    });

    const hasSessions = sessionEntities.length > 0;
    const hasEndpointsOrRoles = endpointOrRoleEntities?.length > 0;
    const hasNoAvailableAssets = !hasSessions && !hasEndpointsOrRoles;

    return (
      <div>
        {hasNoAvailableAssets && (
          <section>
            <InlineMessage>
              <Box className='LandingPage-noAssetsAvailable'>
                <Icon kind='alertCriticalOutline' />
                <H3 className='LandingPage-noAssetsAvailable-title'>
                  {noAssetsAvailableMsg}
                </H3>
              </Box>
            </InlineMessage>
          </section>
        )}
        {hasSessions && !isHideSessionsChecked && (
          <section>
            <div className='LandingPage-listrow-section-title'>
              <div className='LandingPage-listrow-section-title-startLine' />
              <div className='LandingPage-listrow-section-title-label'>
                {sessionsHeader}
              </div>
              <div className='LandingPage-listrow-section-title-endLine' />
            </div>

            <div className='LandingPage-listrow-section LandingPage-listrow-section-sessions'>
              {sessionEntities.map((listItemObj, i) => (
                <ListRowWrapper
                  key={listItemObj.requestAccessKey}
                  index={i}
                  kind={LISTROW_TYPES.SESSION}
                  listItemObj={listItemObj}
                  isCheckInModalOpen={isCheckInModalOpen}
                  setIsCheckInModalOpen={setIsCheckInModalOpen}
                  setIconClickedForRow={setIconClickedForRow}
                  setCheckInModalKind={setCheckInModalKind}
                  isSelected={rowIsSelected}
                  listRowOnClick={listRowOnClick}
                  listRowData={listRowData}
                  setListRowData={setListRowData}
                  setIsExtendModalOpen={setIsExtendModalOpen}
                  onLaunchSession={verifyMfaAndSetSessionToLaunch}
                  setIsSessionTargetModalVisible={
                    setIsSessionTargetModalVisible
                  }
                  setSessionDetailsForAd={setSessionDetailsForAd}
                  updateEntityCheckedStatus={() => {}}
                />
              ))}
            </div>
          </section>
        )}

        {hasEndpointsOrRoles && (
          <section>
            <div className='LandingPage-listrow-section-title'>
              <div className='LandingPage-listrow-section-title-startLine' />
              <div className='LandingPage-listrow-section-title-label'>
                {availableAssetsHeader}
              </div>
              <div className='LandingPage-listrow-section-title-endLine' />
            </div>
            {recordsCount - sessionEntities.length > MAX_RECORDS && (
              <MoreRecordsMessage
                localize={localize}
                isJitForApps={isJitForApps}
              />
            )}
            <div className='LandingPage-listrow-section LandingPage-listrow-section-endpoints'>
              {endpointOrRoleEntities?.map((listItemObj, i) => (
                <ListRowWrapper
                  // eslint-disable-next-line react/no-array-index-key -- endpointKey not unique across types.
                  key={`${listItemObj.endpointKey}-${i}`}
                  index={i}
                  kind={LISTROW_TYPES.ENDPOINT}
                  listItemObj={listItemObj}
                  isCheckInModalOpen={isCheckInModalOpen}
                  setIsCheckInModalOpen={setIsCheckInModalOpen}
                  setIconClickedForRow={setIconClickedForRow}
                  setCheckInModalKind={setCheckInModalKind}
                  isSelected={rowIsSelected}
                  listRowOnClick={listRowOnClick}
                  listRowData={listRowData}
                  setListRowData={setListRowData}
                  setIsExtendModalOpen={setIsExtendModalOpen}
                  onLaunchSession={verifyMfaAndSetSessionToLaunch}
                  setIsSessionTargetModalVisible={
                    setIsSessionTargetModalVisible
                  }
                  setSessionDetailsForAd={setSessionDetailsForAd}
                  updateEntityCheckedStatus={handleCheckboxChange}
                  isJitForApps={isJitForApps}
                  selectedRoles={selectedRoles}
                />
              ))}
            </div>
          </section>
        )}
      </div>
    );
  };

  const handleRequestButtonClick = (page) => {
    if (page === MODAL_PAGE_KINDS.COC) {
      history.push(`${REQUEST_BASE_URL}/check-out-credential`);
    } else if (page === MODAL_PAGE_KINDS.CTS) {
      history.push(`${REQUEST_BASE_URL}/connect-to-session`);
    } else if (page === MODAL_PAGE_KINDS.COR) {
      history.push(`${REQUEST_BASE_URL}/check-out-role`);
    } else {
      Logger.error('Invalid pam page kind');
    }
  };

  // Find the index of the currentListItem to know where we are in the list
  const currentIndex = useMemo(() => {
    if (!currentListItem || !paginatedEntities) return -1;

    return paginatedEntities.findIndex((item) => rowIsSelected(item));
  }, [currentListItem, paginatedEntities]);

  const handlePreviousRow = () => {
    if (currentIndex > 0)
      setCurrentListItem(paginatedEntities[currentIndex - 1]);
  };

  const handleNextRow = () => {
    if (currentIndex >= 0 && currentIndex < paginatedEntities.length - 1)
      setCurrentListItem(paginatedEntities[currentIndex + 1]);
  };

  const isPreviousDisabled = currentIndex <= 0;
  const isNextDisabled =
    // eslint-disable-next-line no-unsafe-optional-chaining
    currentIndex === paginatedEntities?.length - 1 || currentIndex === -1;

  const getAlertBannerClassName = () => {
    let className = 'LandingPage-alertBannerContainer';

    if (isMenuPinned) {
      className += ' LandingPage-alertBannerContainer-leftNav-expanded';
    }

    if (isSidePanelOpen) {
      className += ' LandingPage-alertBannerContainer-sidepanelOpen';
    }

    return className;
  };

  const renderFooter = () => {
    const roleNamesList = selectedRoles.map(
      (item) => item.roleDisplayName || item.roleName
    );

    return (
      <FooterActionBar className='LandingPage-footer'>
        <Box className='LandingPage-footerTitle'>
          <Typography
            kind='body1'
            color='--color-background-neutral-subtlest'
            className='LandingPage-footerTitleText'>
            {`${localize(msgs.pam.landingPage.selected)} (${
              selectedRoles?.length
            }):`}
          </Typography>
          <Box className='LandingPage-footerRoleNamesContainer'>
            <Typography
              kind='body1'
              lineLimit={1}
              color='--color-background-neutral-subtlest'>
              {roleNamesList.join(', ')}
            </Typography>
          </Box>
        </Box>
        <Box
          className={classnames(
            'LandingPage-footer-buttons',
            isMenuPinned && 'LandingPage-footer-buttons--menupinned'
          )}>
          <Button
            type='button'
            kind='ghost'
            size='medium'
            dataTheme='dark'
            onClick={() => {
              dispatch(clearSelectedRoles());
              role.setSelected([]);
            }}>
            {localize(msgs.pam.landingPage.clearSelection)}
          </Button>
          <Button
            type='button'
            kind='outlined'
            size='medium'
            dataTheme='dark'
            onClick={() => handleRequestButtonClick(MODAL_PAGE_KINDS.COR)}>
            {`${checkOutRoles} (${selectedRoles?.length})`}
          </Button>
        </Box>
      </FooterActionBar>
    );
  };

  return (
    <div
      className={classnames(
        'LandingPage LandingPage--pam',
        leftNavGen2 === '1' && 'LandingPage--leftNavGen2'
      )}
      data-testid='LandingPage'>
      <div className='LandingPage-layout'>
        <section ref={landingPageContentRef} className={contentClasses}>
          <section className={titleContainerClasses}>
            <div className='LandingPage-title'>
              {isJitForApps ? landingPageTitleJfa : landingPageTitleDefault}
            </div>
            <div className='LandingPage-description'>
              {isJitForApps ? landingPageDescJfa : landingPageDescDefault}
            </div>
          </section>
          <section className='LandingPage-barPillsAndPage'>
            <div className='LandingPage-barAndPage'>
              <div className='LandingPage-searchBarContainer'>
                <div className='LandingPage-searchBar'>
                  <InputField
                    name='basic'
                    kind='search'
                    placeholder={
                      isJitForApps
                        ? searchInputPlaceholderTextJfa
                        : searchInputPlaceholderText
                    }
                    prefixIcon={
                      <Icon
                        kind='search'
                        color='neutral-600'
                        size='smallMedium'
                      />
                    }
                    value={searchBarValue}
                    setValue={(value) => {
                      setSearchBarValue(value);
                      dispatch(updateSearchValue(value));
                    }}
                  />
                </div>
                {/* TODO: currently a dead button for jit for apps; remove condition after button has functionality */}
                {!isJitForApps && (
                  <div className='LandingPage-filterButton'>
                    <ButtonSelect
                      label={
                        isJitForApps
                          ? localize(msgs.pam.landingPage.view)
                          : localize(msgs.pam.landingPage.filter)
                      }
                      isOpen={isFilterPillsVisible}
                      size='large'
                      prefixIcon={
                        <Icon
                          kind={isJitForApps ? 'Edit' : 'Filter'}
                          color='neutral-600'
                        />
                      }
                      onClick={() => {
                        setIsFilterPillsVisible(!isFilterPillsVisible);
                      }}
                      dataTestId='LandingPage-filter'
                      BadgeComp={
                        isFilterChecked ? (
                          <Badge kind='dot' color='primary' size='small' />
                        ) : null
                      }
                    />
                  </div>
                )}
              </div>
            </div>
            <section
              className={`LandingPage-filterPills ${
                !isFilterPillsVisible ? 'LandingPage-filterPills--isClosed' : ''
              }`}>
              {renderFilterPills(
                MenuMulti,
                ButtonSelect,
                platform,
                asset,
                role,
                isJitForApps,
                hasSelectedRoles(selectedRoles)
              )}
              {!isJitForApps && isFilterChecked ? (
                <Link
                  href='Filter Clear Button'
                  text={localize(msgs.pam.landingPage.clear)}
                  kind='button'
                  onClick={() => {
                    clearAllFilters();
                  }}
                />
              ) : null}
              {paginatedEntities?.filter(
                (item) => item?.type === LISTROW_TYPES.SESSION
              ).length > 0 && (
                <div className='LandingPage-hideSessions'>
                  <Checkbox
                    id='hide-sessions'
                    name='hideSessions'
                    label={hideSessionsLabel}
                    size='small'
                    isChecked={isHideSessionsChecked}
                    onChange={handleChange}
                    showHoverEffect
                    textPosition='after'
                    dataTestId='hideSessions'
                  />
                </div>
              )}
            </section>
          </section>
          <section className={listRowContainerClasses}>
            {renderListOrNoData()}
          </section>
          {hasSelectedRoles(selectedRoles) && (
            <section>{renderFooter()}</section>
          )}
        </section>
      </div>
      {isSidePanelOpen && (
        <aside className='LandingPage-sidePanelContainer' ref={sidePanelRef}>
          <LandingPageSidePanel
            data={currentListItem}
            isOpen={isSidePanelOpen}
            onClose={sidePanelOnClose}
            credentialRequestOnClick={handleRequestButtonClick}
            // TODO: (ROLES) combine these 2 props as possibly 'onRequestButtonClick'
            checkoutRoleOnClick={handleRequestButtonClick}
            setIsCheckInModalOpen={setIsCheckInModalOpen}
            setIconClickedForRow={setIconClickedForRow}
            setCheckInModalKind={setCheckInModalKind}
            setIsExtendModalOpen={setIsExtendModalOpen}
            setSessionToLaunch={verifyMfaAndSetSessionToLaunch}
            setIsSessionTargetModalVisible={setIsSessionTargetModalVisible}
            setSessionDetailsForAd={setSessionDetailsForAd}
            mediaQueryValue={mediaQueryValue}
            handlePreviousRow={handlePreviousRow}
            handleNextRow={handleNextRow}
            isPreviousDisabled={isPreviousDisabled}
            isNextDisabled={isNextDisabled}
            isBulkRolesSelectMode={hasSelectedRoles(selectedRoles)}
            isJitForApps={isJitForApps}
            className='LandingPage-sidePanel'
          />
        </aside>
      )}
      {isCheckInModalOpen && (
        <CheckInModal
          setIsModalOpen={setIsCheckInModalOpen}
          isModalOpen={isCheckInModalOpen}
          row={iconClickedForRow}
          setListRowData={setListRowData}
          kind={checkInModalKind}
          setCheckInModalKind={setCheckInModalKind}
          userName={userName}
          setAlertBannerData={setAlertBannerData}
          isJitForApps={isJitForApps}
        />
      )}
      {isExtendModalOpen && (
        <Extend
          isExtendModalOpen={isExtendModalOpen}
          setIsExtendModalOpen={setIsExtendModalOpen}
          row={iconClickedForRow}
          setAlertBannerData={setAlertBannerData}
          setListRowData={setListRowData}
          userName={userName}
          isJitForApps={isJitForApps}
        />
      )}
      <section className={getAlertBannerClassName()}>
        {alertBannerData.map((banner, index) => (
          <AlertBanner
            key={banner.description}
            colorTheme={banner.colorTheme}
            title={banner.title}
            description={banner.description}
            isVisible={banner.isVisible}
            onCancel={() => {
              // set isVisible to false for the animation.
              setAlertBannerData((prevState) =>
                prevState.map((currBanner, i) =>
                  i === index ? { ...currBanner, isVisible: false } : currBanner
                )
              );
            }}
            isColonVisible={false}
            shouldAutoDismiss={banner?.shouldAutoDismiss ?? true}
            autoDismissDelay={5000}
          />
        ))}
      </section>
      {sessionToLaunch && <SessionLauncher session={sessionToLaunch} />}
      {/* TODO: Update to render/ visible for correct conditions */}
      {isSessionTargetModalVisible && (
        <SessionTargetDetailsModalIsland
          setSessionToLaunch={verifyMfaAndSetSessionToLaunch}
          sessionDetailsForAd={sessionDetailsForAd}
          isSessionTargetModalVisible={isSessionTargetModalVisible}
          setIsSessionTargetModalVisible={setIsSessionTargetModalVisible}
          sessionTargetDetailsModalContentRef={
            sessionTargetDetailsModalContentRef
          }
          sectionRefIsScrolled={sessionTargetDetailsModalSectionRefIsScrolled}
        />
      )}
    </div>
  );
}

export default LandingPage;
